// The live could-help route (bead hf-e5-availability-ccp.2).
//
// Subscription only — there is no mutation here, and that is by design rather than by
// omission. Asking creates a Direct Request, and that write lives in
// `features/requests`' `NeedAskPanel`, which renders this route and fills `onAsk`.
// Keeping the write out of this file is what lets its tests assert that the could-help
// surface cannot move a coverage chip: there is nothing here that could.
//
// ── REACTIVITY IS THE ACCEPTANCE CRITERION, AND IT IS FREE ─────────────────────
//
// "Newly recorded availability appears reactively." Nothing here implements that:
// `matchesForNeed` reads the availability rows, so a member recording their week in
// `hf-e5-availability-ccp.1`'s entry screen re-runs this query in the organiser's open
// browser with no shared state and no import between the two features. The seam
// between ccp.1 and ccp.2 is the database, which is the only seam that cannot drift.
//
// ── AND WHAT MUST NOT HAPPEN WHEN IT DOES ─────────────────────────────────────
//
// Availability changes this list and NOTHING else. It must never move a coverage chip:
// availability is not consent (invariant 2, AVL-026), and a need reading "Covered"
// because somebody said they were free would be the most damaging bug this product
// could ship. That is asserted in `matchingSurfaces.test.tsx` rather than left to the
// fact that this file happens not to write anything.

import { useQuery } from "convex/react";

import type { Id } from "../../../../../convex/_generated/dataModel";

import { matchingQueries } from "../../convex/matching";
import { CouldHelpList } from "./MatchingSurfaces";
import { toCandidateViews, type CandidateView } from "./matchingData";

export interface MatchingRouteProps {
  readonly circleId: string;
  /**
   * Typed as the table's own id because `matchesForNeed` takes `v.id("supportNeeds")`
   * rather than a string. The route's caller holds a plain string from the URL, so the
   * narrowing happens once, here, at the edge — not scattered through the surfaces.
   */
  readonly supportNeedId: string;
  /** The circle's IANA zone. Freshness dates are ITS days (invariant 9). */
  readonly timeZone: string;
  /** Server-decided: may this person send an ask? Never inferred here (AUT-007). */
  readonly canAsk: boolean;
  /**
   * People with an open ask on this need, from `requests.requestsForNeed`'s `awaiting`.
   *
   * Passed in rather than queried again so the organiser's two views of "who has been
   * asked" cannot disagree — `NeedAskPanel` reads it once and feeds both this list and
   * the audience panel beneath it. Which asks count as open is decided server-side,
   * derived from `expiresAt` on every read; nothing in this browser judges a deadline.
   */
  readonly askedPersonIds?: ReadonlySet<string>;
  /** Names the plan already holds, for proxy provenance. */
  readonly namesByPersonId?: ReadonlyMap<string, string>;
  /**
   * What happens when an organiser chooses one name.
   *
   * Passed straight through to `CouldHelpList`. Absent means the list is offered with
   * no action at all — which is a real placement, not a degraded one.
   */
  readonly onAsk?: (candidate: CandidateView) => void;
}

export function MatchingRoute({
  circleId,
  supportNeedId,
  timeZone,
  canAsk,
  askedPersonIds,
  namesByPersonId,
  onAsk,
}: MatchingRouteProps) {
  const candidates = useQuery(matchingQueries.matchesForNeed, {
    circleId,
    supportNeedId: supportNeedId as Id<"supportNeeds">,
  });

  // `undefined` is Convex still loading. The need detail owns its own loading and
  // unavailable surfaces, so this renders nothing rather than a second spinner.
  if (candidates === undefined) return null;

  return (
    <CouldHelpList
      canAsk={canAsk}
      {...(onAsk === undefined ? {} : { onAsk })}
      candidates={toCandidateViews(candidates, {
        timeZone,
        ...(askedPersonIds === undefined ? {} : { askedPersonIds }),
        ...(namesByPersonId === undefined ? {} : { namesByPersonId }),
      })}
    />
  );
}
